import { useEffect } from "react";
import { api } from "@/lib/api";

const WAKE = /\b(hey|hi|ok|okay|hola|oye)?[\s,]*mi\s?lu\b/i;
const SPEAKING = 0.02;
const QUIET_MS = 700;
const MOST_MS = 6000;

// An ear kept open for "Hey MiLu": short clips only, and only when something is said.
// two soft notes, made on the spot — no sound file to load
// "wake" rises when he hears his name; "end" falls, quieter, when a call closes
export const chime = (kind = "wake") => {
  const notes = kind === "end" ? [1320, 880] : [880, 1320];
  const peak = kind === "end" ? 0.07 : 0.12;
  try {
    const box = new (window.AudioContext || window.webkitAudioContext)();
    notes.forEach((note, i) => {
      const tone = box.createOscillator();
      const level = box.createGain();
      tone.type = "sine";
      tone.frequency.value = note;
      const at = box.currentTime + i * 0.13;
      level.gain.setValueAtTime(0.0001, at);
      level.gain.exponentialRampToValueAtTime(peak, at + 0.03);
      level.gain.exponentialRampToValueAtTime(0.0001, at + 0.22);
      tone.connect(level).connect(box.destination);
      tone.start(at);
      tone.stop(at + 0.24);
    });
    setTimeout(() => box.close(), 800);
  } catch {
    /* a silent chime is no loss */
  }
};

export const useWakeWord = (on, onWake) => {
  useEffect(() => {
    if (!on) return undefined;
    let alive = true;
    const gear = {};

    const round = () => {
      if (!alive || !gear.recorder || gear.recorder.state === "recording") return;
      const bits = [];
      const rec = gear.recorder;
      rec.ondataavailable = (e) => e.data.size && bits.push(e.data);
      rec.onstop = async () => {
        clearInterval(gear.watch);
        if (!alive) return;
        const clip = new Blob(bits, { type: "audio/webm" });
        if (clip.size < 2000) return round();
        try {
          const form = new FormData();
          form.append("audio", clip, "wake.webm");
          const { data } = await api.post("/wingman/listen", form, { timeout: 45000 });
          if (!alive) return;
          if (WAKE.test(data.text || "")) {
            alive = false;
            chime();
            onWake();
            return;
          }
        } catch {
          /* a missed word is no drama */
        }
        round();
      };

      rec.start();
      const began = Date.now();
      let spoke = 0;
      let hushed = 0;
      gear.watch = setInterval(() => {
        const loud = gear.meter();
        if (loud > SPEAKING) {
          spoke += 90;
          hushed = 0;
        } else if (spoke > 300) {
          hushed += 90;
        }
        if ((hushed >= QUIET_MS && spoke > 300) || Date.now() - began > MOST_MS) {
          clearInterval(gear.watch);
          if (rec.state === "recording") rec.stop();
        }
      }, 90);
    };

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true },
        });
        const ears = new (window.AudioContext || window.webkitAudioContext)();
        const gauge = ears.createAnalyser();
        gauge.fftSize = 512;
        ears.createMediaStreamSource(stream).connect(gauge);
        const frame = new Uint8Array(gauge.fftSize);
        Object.assign(gear, {
          stream, ears,
          recorder: new MediaRecorder(stream),
          meter: () => {
            gauge.getByteTimeDomainData(frame);
            let sum = 0;
            for (const v of frame) sum += ((v - 128) / 128) ** 2;
            return Math.sqrt(sum / frame.length);
          },
        });
        round();
      } catch {
        /* no microphone, no wake word */
      }
    })();

    return () => {
      alive = false;
      clearInterval(gear.watch);
      if (gear.recorder?.state === "recording") gear.recorder.stop();
      gear.stream?.getTracks().forEach((t) => t.stop());
      gear.ears?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on]);
};
